import { MetadataFormData } from "@saleor/components/Metadata";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import { ProductVariant } from "@saleor/fragments/types/ProductVariant";
import useForm, { FormChange, SubmitPromise } from "@saleor/hooks/useForm";
import useFormset, {
  FormsetChange,
  FormsetData
} from "@saleor/hooks/useFormset";
import { GetErpProductVariant_erpProductVariant } from "@saleor/products/types/GetErpProductVariant";
import {
  getAttributeInputFromSku,
  getAttributeInputFromVariant,
  getStockInputFromVariant
} from "@saleor/products/utils/data";
import { SearchWarehouses_search_edges_node } from "@saleor/searches/types/SearchWarehouses";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import { mapMetadataItemToInput } from "@saleor/utils/maps";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { diff } from "fast-array-diff";
import React from "react";

import { maybe, splitDateTime } from "../../../misc";
import handleFormSubmit from "../../../utils/handlers/handleFormSubmit";
import { SkuAttributeInputData } from "../ProductSkuAttributes";
import { ProductStockInput } from "../ProductStocks";
import { VariantAttributeInputData } from "../ProductVariantAttributes";

export interface ProductVariantUpdateFormData extends MetadataFormData {
  costPrice: string;
  price: string;
  sku: string;
  trackInventory: boolean;
  weight: string;
  // extraCharge: string;
  fromColor: string;
  isTwoColor: boolean;
  toColor: string;
  colorThumbnail: string;
  shortDescription: string;
  fullDescription: string;
  describe: string;
  USP: string;
  skuName: string;
  slug: string;
  seoTitle: string;
  seoKeyword: string;
  seoDescription: string;
  framesImg: string;
  framesVideo: string;
  isPublished: boolean;
  publicationDate: string;
  availableForPurchaseDate: string;
  availableForPurchaseTime: string;
  isAvailableForPurchase: boolean;
  cornermark: string;
  extraChargeCategory: string;
  preOrderDate: string;
  preOrderTime: string;
  isPreOrder: boolean;
  colorTagText: string;
  // colorTagIcon: string;
  colorTagBackground: string;
  colorTagTextColor: string;
  // 定义不使用
  visibleInListings: boolean;
}
export interface ProductVariantUpdateData extends ProductVariantUpdateFormData {
  attributes: FormsetData<VariantAttributeInputData, string>;
  skuAttributes: FormsetData<SkuAttributeInputData, string>;
  stocks: ProductStockInput[];
}
export interface ProductVariantUpdateSubmitData
  extends ProductVariantUpdateFormData {
  attributes: FormsetData<VariantAttributeInputData, string>;
  skuAttributes: FormsetData<SkuAttributeInputData, string>;
  addStocks: ProductStockInput[];
  updateStocks: ProductStockInput[];
  removeStocks: string[];
}

export interface UseProductVariantUpdateFormOpts
  extends Record<
    "cornermarks" | "extracharges",
    SingleAutocompleteChoiceType[]
  > {
  setSelectedCornermark: React.Dispatch<React.SetStateAction<string>>;
  setSelectedExtracharge: React.Dispatch<React.SetStateAction<string>>;
  warehouses: SearchWarehouses_search_edges_node[];
}

export interface UseProductVariantUpdateFormResult {
  change: FormChange;
  data: ProductVariantUpdateData;
  handlers: Record<
    "changeStock" | "selectAttribute" | "selectSkuAttribute",
    FormsetChange
  > &
    Record<"addStock" | "deleteStock", (id: string) => void> & {
      changeMetadata: FormChange;
      selectCornermark: FormChange;
      selectExtracharge: FormChange;
    };
  hasChanged: boolean;
  submit: () => void;
}

export interface ProductVariantUpdateFormProps
  extends UseProductVariantUpdateFormOpts {
  children: (props: UseProductVariantUpdateFormResult) => React.ReactNode;
  variant: ProductVariant;
  colorThumbnail: string;
  // colorTagIcon: string;
  onSubmit: (data: ProductVariantUpdateSubmitData) => SubmitPromise;
  erpProductVariant: GetErpProductVariant_erpProductVariant;
}
const getAvailabilityData = ({
  availableForPurchaseDate,
  isAvailableForPurchase,
  isPreOrder,
  isPublished,
  publicationDate
}: ProductVariantUpdateFormData) => ({
  isAvailableForPurchase: isAvailableForPurchase || !!availableForPurchaseDate,
  isPreOrder,
  isPublished: isPublished || !!publicationDate
});

function useProductVariantUpdateForm(
  variant: ProductVariant,
  colorThumbnail: string,
  // colorTagIcon: string,
  erpProductVariant: GetErpProductVariant_erpProductVariant,
  onSubmit: (data: ProductVariantUpdateSubmitData) => SubmitPromise,
  opts: UseProductVariantUpdateFormOpts
): UseProductVariantUpdateFormResult {
  const [changed, setChanged] = React.useState(false);
  const triggerChange = () => setChanged(true);

  const attributeInput = getAttributeInputFromVariant(
    variant,
    erpProductVariant
  );
  const skuAttributeInput = getAttributeInputFromSku(
    variant,
    erpProductVariant
  );
  const stockInput = getStockInputFromVariant(variant);

  const initial: ProductVariantUpdateFormData = {
    costPrice: variant?.costPrice?.amount.toString() || "",
    metadata: variant?.metadata?.map(mapMetadataItemToInput),
    price: variant?.price?.amount.toString() || "",
    privateMetadata: variant?.privateMetadata?.map(mapMetadataItemToInput),
    sku: variant?.sku || "",
    trackInventory: variant?.trackInventory,
    weight:
      erpProductVariant?.weight || variant?.weight?.value.toString() || "",
    // extraCharge: variant?.extraCharge || "{}",
    fromColor: variant?.fromColor || "",
    isTwoColor: maybe(() => variant.isTwoColor, false),
    toColor: variant?.toColor || "",
    // colorThumbnail: variant?.colorThumbnail || "",
    colorThumbnail: colorThumbnail || "",
    shortDescription: variant?.shortDescription || "",
    fullDescription: variant?.fullDescription || "",
    isPublished: maybe(() => variant.isPublished, false),
    publicationDate: maybe(() => variant.publicationDate, ""),
    cornermark: variant?.cornerMark?.id || "",
    extraChargeCategory: variant?.extraChargeCategory?.categoryName || "",
    skuName: erpProductVariant?.alias || variant?.skuName || "",
    slug: variant?.slug || "",
    seoTitle: variant?.seoTitle || "",
    seoKeyword: variant?.seoKeyword || "",
    seoDescription: variant?.seoDescription || "",
    describe: variant?.describe || "",
    USP: variant?.USP || "",
    framesImg: variant?.framesImg || "",
    framesVideo: variant?.framesVideo || "",
    colorTagText: variant?.colorTagText || "",
    colorTagBackground: variant?.colorTagBackground || "",
    colorTagTextColor: variant?.colorTagTextColor || "",
    // colorTagIcon: variant?.colorTagIcon || '',
    // colorTagIcon: colorTagIcon || "",

    availableForPurchaseDate: splitDateTime(
      maybe(() => variant?.availableForPurchase, "")
    ).date,
    availableForPurchaseTime: splitDateTime(
      maybe(() => variant?.availableForPurchase, "")
    ).time,
    isAvailableForPurchase: maybe(() => variant?.isAvailableForPurchase, true),

    preOrderDate: splitDateTime(maybe(() => variant?.preOrder, "")).date,
    preOrderTime: splitDateTime(maybe(() => variant?.preOrder, "")).time,
    isPreOrder: maybe(() => variant?.isPreOrder, false),

    // 定义不使用
    visibleInListings: undefined
  };

  const form = useForm(initial);
  const attributes = useFormset(attributeInput);
  const skuAttributes = useFormset(skuAttributeInput);
  const stocks = useFormset(stockInput);
  const {
    isMetadataModified,
    isPrivateMetadataModified,
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const handleChange: FormChange = (event, cb) => {
    form.change(event, cb);
    triggerChange();
  };
  const changeMetadata = makeMetadataChangeHandler(handleChange);
  const handleAttributeChange: FormsetChange = (id, value) => {
    attributes.change(id, value);
    triggerChange();
  };
  const handleSkuAttributeChange: FormsetChange = (id, value) => {
    skuAttributes.change(id, value);
    triggerChange();
  };
  const handleCornermarkSelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedCornermark,
    opts.cornermarks
  );
  const handleExtrachargeSelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedExtracharge,
    opts.extracharges
  );
  const handleStockAdd = (id: string) => {
    triggerChange();
    stocks.add({
      data: null,
      id,
      label: opts.warehouses.find(warehouse => warehouse.id === id).name,
      value: "0"
    });
  };
  const handleStockChange = (id: string, value: string) => {
    triggerChange();
    stocks.change(id, value);
  };
  const handleStockDelete = (id: string) => {
    triggerChange();
    stocks.remove(id);
  };
  const metadata = isMetadataModified ? form.data.metadata : undefined;
  const privateMetadata = isPrivateMetadataModified
    ? form.data.privateMetadata
    : undefined;

  const dataStocks = stocks.data.map(stock => stock.id);
  const variantStocks = variant?.stocks.map(stock => stock.warehouse.id) || [];
  const stockDiff = diff(variantStocks, dataStocks);

  const addStocks = stocks.data.filter(stock =>
    stockDiff.added.some(addedStock => addedStock === stock.id)
  );
  const updateStocks = stocks.data.filter(
    stock => !stockDiff.added.some(addedStock => addedStock === stock.id)
  );

  const data: ProductVariantUpdateData = {
    ...form.data,
    attributes: attributes.data,
    skuAttributes: skuAttributes.data,
    stocks: stocks.data
  };
  const submitData: ProductVariantUpdateSubmitData = {
    ...form.data,
    ...getAvailabilityData(form.data),
    addStocks,
    attributes: attributes.data,
    skuAttributes: skuAttributes.data,
    metadata,
    privateMetadata,
    removeStocks: stockDiff.removed,
    updateStocks
  };

  const submit = () => handleFormSubmit(submitData, onSubmit, setChanged);

  return {
    change: handleChange,
    data,
    handlers: {
      addStock: handleStockAdd,
      changeMetadata,
      changeStock: handleStockChange,
      deleteStock: handleStockDelete,
      selectAttribute: handleAttributeChange,
      selectSkuAttribute: handleSkuAttributeChange,
      selectCornermark: handleCornermarkSelect,
      selectExtracharge: handleExtrachargeSelect
    },
    hasChanged: changed,
    submit
  };
}

const ProductVariantUpdateForm: React.FC<ProductVariantUpdateFormProps> = ({
  children,
  variant,
  colorThumbnail,
  // colorTagIcon,
  erpProductVariant,
  onSubmit,
  ...rest
}) => {
  const props = useProductVariantUpdateForm(
    variant,
    colorThumbnail,
    // colorTagIcon,
    erpProductVariant,
    onSubmit,
    rest
  );

  return <form onSubmit={props.submit}>{children(props)}</form>;
};

ProductVariantUpdateForm.displayName = "ProductVariantUpdateForm";
export default ProductVariantUpdateForm;
